import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { Button, Card, Checkbox, Cluster, EmptyState, HintMark, Input, Modal, Select, Stack, Tabs, guardSubmit, useToast } from "@salesapp/ui";
import { reportSaveBlocked, reportSaveFailure, reportSaved } from "../lib/saveNotice";
import { useAuth } from "../shell/AuthProvider";
import { useAccess } from "../shell/AccessProvider";
import { orgSettingsApi, type AuditEvent, type ImportPreview, type OrgAppSettings } from "../lib/orgSettingsApi";
import { leadGenApi, type LeadGenSearchLimit, type LeadGenSearchLimits } from "../lib/leadGenApi";
import { MailboxSettingsCard, MailboxAiSettings } from "./crm/MailboxSettingsCard";
import { blobToDataUrl } from "../lib/blobDataUrl";
import { opsApi, type RecycleItem, type Territory } from "../lib/opsApi";
import { AiConnectionPanel } from "./SettingsAiPanel";
import { SettingsLoxleyPanel } from "../loxley/SettingsLoxleyPanel";
import { LoxleyGuardrailsPanel } from "../loxley/LoxleyGuardrailsPanel";
import { billingApi, type SsoSettings } from "../lib/billingApi";
import { PeoplePanel } from "./SettingsPeoplePanel";
import { BillingPanel } from "./SettingsBillingPanel";
import { NetworkPanel } from "./SettingsNetworkPanel";
import { notifyLocaleChanged, useLocale } from "../shell/LocaleProvider";
import {
  DATE_FORMAT_OPTIONS,
  NUMBER_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  asDateFormat,
  asNumberFormat,
  asTimeFormat,
  formatPreview,
  supportedTimeZones,
} from "../lib/localeFormat";
import { FIELD_HINTS } from "../help/fieldHints";
import { ActivityTypesSettings } from "./ActivityTypesSettings";
import { sortedCountries } from "../lib/countries";
import "./pages.css";
import "./crm/crm.css";

function timezoneGroups(zones: string[]): Array<{ region: string; zones: string[] }> {
  const buckets = new Map<string, string[]>();
  for (const zone of zones) {
    const slash = zone.indexOf("/");
    if (slash === -1) continue;
    const region = zone.slice(0, slash);
    const list = buckets.get(region);
    if (list) list.push(zone);
    else buckets.set(region, [zone]);
  }
  return [...buckets.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([region, items]) => ({ region, zones: items }));
}

/** Real dropdown. A datalist arrow does not open in Chrome once a zone is already filled in. */
function TimezoneSelect({ zones, value, onChange }: { zones: string[]; value: string; onChange: (zone: string) => void }) {
  const known = new Set(zones);
  const ungrouped = zones.filter((zone) => !zone.includes("/"));
  const groups = timezoneGroups(zones);
  return (
    <Select
      label="Timezone"
      tooltip={FIELD_HINTS.org_timezone}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {value && !known.has(value) ? <option value={value}>{value}</option> : null}
      {ungrouped.map((zone) => (
        <option key={zone} value={zone}>{zone}</option>
      ))}
      {groups.map((group) => (
        <optgroup key={group.region} label={group.region}>
          {group.zones.map((zone) => (
            <option key={zone} value={zone}>{zone}</option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}

export function SettingsPage() {
  const { session } = useAuth();
  const { can, accessBlocked } = useAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = session?.orgId ?? "";
  const canPeople = Boolean(session?.isOrgAdmin);
  const canCrmImport = can("crm.companies", "configure");
  const isCrmAppAdmin = can("crm.field_options", "configure") || can("crm.companies", "delete");
  const canHistoricCrm = canCrmImport || isCrmAppAdmin;
  const canDesign = can("crm.app.design", "design");
  const requestedRaw = searchParams.get("tab");
  const requested = requestedRaw === "people" ? "users"
    : requestedRaw === "territories" ? "locale"
    : requestedRaw === "loxley" ? "ai"
    : requestedRaw;
  const defaultTab = canPeople ? "users" : "import";
  const [tab, setTab] = useState(requested || defaultTab);

  // A bare /settings (no ?tab=) must land on the default tab even when the
  // user is already on Settings with a different tab active — clearing the
  // query param should reset state, not leave the previous tab stuck.
  useEffect(() => {
    setTab(requested || defaultTab);
  }, [requested, defaultTab]);

  useEffect(() => {
    if (requestedRaw !== "territories" && requestedRaw !== "loxley") return;
    const next = new URLSearchParams(searchParams);
    if (requestedRaw === "territories") next.set("tab", "locale");
    if (requestedRaw === "loxley") {
      next.set("tab", "ai");
      next.set("section", "loxley");
    }
    setSearchParams(next, { replace: true });
  }, [requestedRaw, searchParams, setSearchParams]);

  if (!canPeople && !canHistoricCrm) {
    return (
      <EmptyState
        title="Settings are for administrators"
        description="Changing users needs an organization admin. Loading historic CRM data needs an organization admin or a CRM app admin."
      />
    );
  }

  const tabs = accessBlocked && canPeople
    ? [{ key: "billing", label: "Plan & Billing" }]
    : [
        ...(canPeople ? [
          { key: "users", label: "Users", dataTour: "people-users-tab" },
          { key: "roles", label: "User Roles", dataTour: "people-roles-tab" },
          { key: "company", label: "Company Info", dataTour: "org-company-tab" },
          { key: "locale", label: "Territories & Locale", dataTour: "org-locale-tab" },
        ] : []),
        ...(canPeople ? [{ key: "app", label: "App Settings", dataTour: "org-app-tab" }] : []),
        { key: "import", label: "Historic Data" },
        ...(canPeople ? [
          { key: "billing", label: "Plan & Billing" },
          { key: "network", label: "Network" },
          { key: "sso-ai", label: "SSO" },
          { key: "ai", label: "AI" },
        ] : []),
        ...(canCrmImport ? [{ key: "recycle", label: "Recycle bin" }] : []),
        ...(canPeople ? [{ key: "audit", label: "Audit log" }] : []),
      ];
  const activeTab = accessBlocked && canPeople ? "billing" : tab;

  return (
    <Stack gap={5}>
      <div>
        <h1 className="home-title" data-tour="settings-heading">Organization settings</h1>
        <p className="home-subtitle">Assign people, named roles, territories, and the organization plan.</p>
      </div>
      <Tabs
        items={tabs}
        activeKey={activeTab}
        onChange={(next) => {
          setTab(next);
          setSearchParams(next === "users" ? {} : { tab: next }, { replace: true });
        }}
      />
      {activeTab === "users" && canPeople && <PeoplePanel orgId={orgId} section="users" />}
      {activeTab === "roles" && canPeople && <PeoplePanel orgId={orgId} section="roles" />}
      {activeTab === "company" && canPeople && <CompanyInfoPanel orgId={orgId} />}
      {activeTab === "locale" && canPeople && <LocaleSettingsPanel orgId={orgId} />}
      {activeTab === "app" && canPeople && <AppSettingsPanel orgId={orgId} />}
      {activeTab === "import" && (
        <ImportPanel orgId={orgId} canPeople={canPeople} canCrmImport={canHistoricCrm} />
      )}
      {activeTab === "recycle" && canCrmImport && <RecyclePanel orgId={orgId} />}
      {activeTab === "audit" && canPeople && <AuditPanel orgId={orgId} />}
      {activeTab === "billing" && canPeople && <BillingPanel orgId={orgId} />}
      {activeTab === "network" && canPeople && <NetworkPanel orgId={orgId} />}
      {activeTab === "sso-ai" && canPeople && <SsoAiPanel orgId={orgId} />}
      {activeTab === "ai" && canPeople && <AiSettingsSections orgId={orgId} />}
      {canDesign && (activeTab === "users" || activeTab === "roles") && (
        <p className="home-subtitle">Field and screen designers live under CRM: App Designer, Fields, Dashboards, and Documents.</p>
      )}
    </Stack>
  );
}

function settingsBody(value: OrgAppSettings) {
  const rateRaw = value.tax_rate;
  const taxRate =
    rateRaw === null || rateRaw === undefined || String(rateRaw).trim() === ""
      ? null
      : rateRaw;
  return {
    timezone: value.timezone,
    currency: value.currency,
    currency_symbol: value.currency_symbol,
    quote_approval_required: value.quote_approval_required,
    company_address_line1: value.company_address_line1 ?? "",
    company_address_line2: value.company_address_line2 ?? "",
    company_address_line3: value.company_address_line3 ?? "",
    company_address_line4: value.company_address_line4 ?? "",
    tax_number: value.tax_number ?? "",
    tax_rate: taxRate,
    fiscal_year_start_month: value.fiscal_year_start_month ?? 1,
    history_after_days: value.history_after_days ?? 1,
    country: value.country ?? "",
    website: value.website ?? "",
    date_format: asDateFormat(value.date_format, value.currency),
    time_format: asTimeFormat(value.time_format, value.currency),
    number_format: asNumberFormat(value.number_format, value.currency),
    forex_pull_enabled: Boolean(value.forex_pull_enabled),
    forex_url: value.forex_url ?? "",
  };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const FALLBACK_CURRENCIES = [
  { code: "AUD", symbol: "$" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "ZAR", symbol: "R" },
  { code: "NZD", symbol: "$" },
  { code: "CAD", symbol: "$" },
  { code: "JPY", symbol: "¥" },
  { code: "INR", symbol: "₹" },
  { code: "CHF", symbol: "CHF" },
  { code: "SGD", symbol: "$" },
];

function useOrgAppSettings(orgId: string) {
  const [value, setValue] = useState<OrgAppSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void orgSettingsApi.getAppSettings(orgId).then(setValue).catch((err) => {
      setError(err instanceof Error ? err.message : "Settings could not be loaded.");
    });
  }, [orgId]);

  return { value, setValue, error, setError, notice, setNotice, saving, setSaving };
}

const GLOBAL_TERRITORY = "global";

function territoryCodeFromName(name: string, taken: Set<string>): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
  const base = !slug || slug === GLOBAL_TERRITORY ? "territory" : slug;
  if (!taken.has(base)) return base;
  for (let n = 2; n < 50; n += 1) {
    const next = `${base.slice(0, 28)}-${n}`;
    if (!taken.has(next)) return next;
  }
  return `${base.slice(0, 24)}-new`;
}

function draftFromTerritory(row: Territory, fallback: OrgAppSettings): OrgAppSettings {
  return {
    ...fallback,
    country: row.country ?? "",
    timezone: row.timezone || fallback.timezone,
    currency: row.currency || fallback.currency,
    currency_symbol: row.currency_symbol ?? fallback.currency_symbol,
    date_format: row.date_format || fallback.date_format,
    time_format: row.time_format || fallback.time_format,
    number_format: row.number_format || fallback.number_format,
    forex_pull_enabled: row.forex_pull_enabled ?? fallback.forex_pull_enabled,
    fiscal_year_start_month: row.fiscal_year_start_month ?? fallback.fiscal_year_start_month,
    quote_approval_required: row.quote_approval_required ?? fallback.quote_approval_required,
    tax_rate: row.tax_rate ?? null,
    history_after_days: row.history_after_days ?? fallback.history_after_days,
  };
}

function territoryLocaleBody(value: OrgAppSettings) {
  const body = settingsBody(value);
  return {
    timezone: body.timezone,
    currency: body.currency,
    currency_symbol: body.currency_symbol,
    country: body.country || null,
    date_format: body.date_format,
    time_format: body.time_format,
    number_format: body.number_format,
    forex_pull_enabled: body.forex_pull_enabled,
    fiscal_year_start_month: body.fiscal_year_start_month,
    quote_approval_required: body.quote_approval_required,
    tax_rate: body.tax_rate,
    history_after_days: body.history_after_days,
  };
}

function LocaleSettingsPanel({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const zones = useMemo(() => supportedTimeZones(), []);
  const { value: orgValue, setValue: setOrgValue, error, setError, notice, setNotice, saving, setSaving } = useOrgAppSettings(orgId);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [selected, setSelected] = useState(GLOBAL_TERRITORY);
  const [draft, setDraft] = useState<OrgAppSettings | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    void opsApi.listTerritories(orgId).then((row) => setTerritories(row.territories)).catch(() => setTerritories([]));
  }, [orgId]);

  if (!orgValue) return error ? <p className="settings-error">{error}</p> : <p>Loading locale settings…</p>;
  const loaded = orgValue;
  const editingGlobal = selected === GLOBAL_TERRITORY || draft == null;
  const value = (editingGlobal ? loaded : draft) ?? loaded;
  const setValue = (next: OrgAppSettings) => {
    if (editingGlobal) setOrgValue(next);
    else setDraft(next);
  };
  const currencies = loaded.currencies?.length ? loaded.currencies : FALLBACK_CURRENCIES;
  const others = territories
    .filter((item) => item.code !== GLOBAL_TERRITORY)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const selectedRow = others.find((item) => item.id === selected);

  function choose(id: string) {
    setSelected(id);
    setNotice(null);
    setError(null);
    if (id === GLOBAL_TERRITORY) {
      setDraft(null);
      return;
    }
    const row = territories.find((item) => item.id === id);
    if (row) setDraft(draftFromTerritory(row, loaded));
  }

  async function addTerritory() {
    const name = newName.trim();
    if (!name) {
      setNameError("Enter a name.");
      return;
    }
    setNameError(null);
    const taken = new Set(territories.map((item) => item.code));
    const copied = territoryLocaleBody(loaded);
    try {
      const created = await opsApi.createTerritory(orgId, {
        code: territoryCodeFromName(name, taken),
        name,
        ...copied,
      });
      setTerritories((current) => [...current.filter((item) => item.id !== created.id), created]);
      setDraft(draftFromTerritory(created, loaded));
      setSelected(created.id);
      setCreating(false);
      setNewName("");
      setNotice(`${created.name} starts with Global's settings. Change them here, then save.`);
    } catch (err) {
      setNameError(err instanceof Error ? err.message : "Territory could not be added.");
    }
  }

  return (
    <Stack gap={4}>
      {error && <p role="alert" className="settings-error">{error}</p>}
      {notice && <p className="home-subtitle">{notice}</p>}
      <Card>
        <form
          onSubmit={guardSubmit((event) => {
            event.preventDefault();
            setSaving(true);
            setError(null);
            const save = editingGlobal
              ? orgSettingsApi.saveAppSettings(orgId, settingsBody(value)).then((row) => {
                setOrgValue(row);
                setNotice("Locale settings saved.");
                reportSaved(show);
                notifyLocaleChanged();
              })
              : opsApi.updateTerritoryLocale(orgId, selected, territoryLocaleBody(value)).then((saved) => {
                setTerritories((current) => current.map((item) => item.id === saved.id ? saved : item));
                setDraft(draftFromTerritory(saved, loaded));
                setNotice(`${saved.name} saved.`);
                reportSaved(show);
              });
            return save.catch((err) => {
              setError(reportSaveFailure(show, err, "Settings could not be saved."));
            }).finally(() => setSaving(false));
          })}
        >
          <Stack gap={4}>
            <Cluster gap={3} align="end">
              <Select
                label="Territory"
                value={editingGlobal ? GLOBAL_TERRITORY : selected}
                onChange={(event) => choose(event.target.value)}
              >
                <option value={GLOBAL_TERRITORY}>Global</option>
                {others.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </Select>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setNameError(null);
                  setNewName("");
                  setCreating(true);
                }}
              >
                New territory
              </Button>
            </Cluster>
            <p className="home-subtitle">
              Global is the organisation default. People can still choose how dates and numbers look on their own
              screen under User settings. A new territory starts as a copy of Global and keeps the same settings.
              Quotes, orders, and invoices for companies in that territory use its tax rate and quote approval.
              Forecast uses its fiscal year when that territory is selected. The open lists follow Global's history wait.
            </p>
            <Stack gap={4} data-tour="org-timezone">
              <Select
                label="Country"
                tooltip="Where your organization is based. Postcode searches (LeadGen) and addresses use this country."
                value={value.country ?? ""}
                onChange={(event) => setValue({ ...value, country: event.target.value })}
              >
                <option value="">Choose your country</option>
                {sortedCountries().map((item) => (
                  <option key={item.code} value={item.code}>{item.name}</option>
                ))}
              </Select>
              <TimezoneSelect
                zones={zones}
                value={value.timezone}
                onChange={(timezone) => setValue({ ...value, timezone })}
              />
            </Stack>
            <div data-tour="org-currency">
              <Select
                label="Currency"
                tooltip={FIELD_HINTS.org_currency}
                value={value.currency}
                onChange={(event) => {
                  const code = event.target.value;
                  const symbol = currencies.find((item) => item.code === code)?.symbol ?? value.currency_symbol;
                  setValue({ ...value, currency: code, currency_symbol: symbol });
                }}
              >
                {currencies.map((item) => (
                  <option key={item.code} value={item.code}>{item.code} ({item.symbol})</option>
                ))}
              </Select>
            </div>
            <Select
              label="Date format"
              tooltip={FIELD_HINTS.org_date_format}
              value={asDateFormat(value.date_format, value.currency)}
              onChange={(event) => setValue({ ...value, date_format: event.target.value })}
            >
              {DATE_FORMAT_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
            <Select
              label="Time format"
              tooltip={FIELD_HINTS.org_time_format}
              value={asTimeFormat(value.time_format, value.currency)}
              onChange={(event) => setValue({ ...value, time_format: event.target.value })}
            >
              {TIME_FORMAT_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
            <Select
              label="Number format"
              tooltip={FIELD_HINTS.org_number_format}
              value={asNumberFormat(value.number_format, value.currency)}
              onChange={(event) => setValue({ ...value, number_format: event.target.value })}
            >
              {NUMBER_FORMAT_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
            <p className="home-subtitle">
              Example: {formatPreview({
                dateFormat: asDateFormat(value.date_format, value.currency),
                timeFormat: asTimeFormat(value.time_format, value.currency),
                numberFormat: asNumberFormat(value.number_format, value.currency),
              })}
            </p>
            <Checkbox
              label="Pull exchange rates every day"
              checked={Boolean(value.forex_pull_enabled)}
              tooltip="Stores European Central Bank rates from the address below for this organization. A quote still keeps the rate you save on it, and you can change that rate."
              onChange={(event) => setValue({ ...value, forex_pull_enabled: event.target.checked })}
            />
            {editingGlobal ? (
              <Input
                label="Exchange rate address"
                value={value.forex_url ?? ""}
                tooltip="Used when a line is priced in another currency and no rate is saved yet. The built-in address is filled in already. Replace it with another https address that returns the same latest-rate JSON."
                onChange={(event) => setValue({ ...value, forex_url: event.target.value })}
              />
            ) : null}
            <div data-tour="org-fiscal-year">
              <Select
                label="Fiscal year starts in"
                tooltip={FIELD_HINTS.org_fiscal_year_start}
                value={String(value.fiscal_year_start_month ?? 1)}
                onChange={(event) =>
                  setValue({ ...value, fiscal_year_start_month: Number(event.target.value) })
                }
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={index + 1}>{name}</option>
                ))}
              </Select>
            </div>
            <p className="home-subtitle">
              {editingGlobal
                ? "Used on Forecast and on the Opportunities and Leads projected charts. January keeps quarters on the calendar year."
                : "Forecast uses this month when this territory is selected. January keeps quarters on the calendar year."}
            </p>
            <div className="settings-long-check" data-tour="org-quote-approval">
              <Checkbox
                label="Enable this option for a Sales Manager to approve a Sales Quote before sending it to a customer."
                checked={value.quote_approval_required}
                tooltip={FIELD_HINTS.org_quote_approval_required}
                onChange={(event) => setValue({ ...value, quote_approval_required: event.target.checked })}
              />
            </div>
            <div data-tour="org-tax-rate">
              <Input
                label="Tax rate (%)"
                tooltip={FIELD_HINTS.org_tax_rate}
                inputMode="decimal"
                placeholder="Leave blank for no tax"
                value={value.tax_rate == null ? "" : String(value.tax_rate)}
                onChange={(event) => setValue({ ...value, tax_rate: event.target.value })}
              />
            </div>
            <p className="home-subtitle">
              Applied to quotes, orders, and invoices. Leave blank to omit tax from those documents.
            </p>
            <Input
              label="Move finished quotes, orders and invoices to History after (days)"
              tooltip={FIELD_HINTS.org_history_after_days}
              inputMode="numeric"
              value={String(value.history_after_days ?? 1)}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, "").slice(0, 3);
                setValue({ ...value, history_after_days: Math.min(365, Number(digits || 0)) });
              }}
            />
            <p className="home-subtitle">
              {editingGlobal
                ? "A quote leaves the open list once its order is created, an order once it is invoiced, and an invoice once it is paid. After this many days they move to the History table below so you can focus on current transactions. Use 0 to move them straight away."
                : "This wait is stored with the territory. The open lists follow the history wait saved on Global."}
            </p>
            <Button type="submit" data-tour="org-save-locale" disabled={saving}>
              {saving ? "Saving…" : editingGlobal ? "Save locale settings" : `Save ${selectedRow?.name ?? "territory"}`}
            </Button>
          </Stack>
        </form>
      </Card>
      <Modal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New territory"
        footer={
          <Cluster gap={2}>
            <Button type="button" variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="button" onClick={() => void addTerritory()}>Create territory</Button>
          </Cluster>
        }
      >
        <Stack gap={3}>
          <p className="home-subtitle">
            The new territory starts with Global's country, currency, tax, quote approval, and the rest of these settings.
          </p>
          <Input label="Name" value={newName} onChange={(event) => setNewName(event.target.value)} />
          {nameError && <p role="alert" className="settings-error">{nameError}</p>}
        </Stack>
      </Modal>
    </Stack>
  );
}

function CompanyInfoPanel({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { value, setValue, error, setError, notice, setNotice, saving, setSaving } = useOrgAppSettings(orgId);
  const [logoSrc, setLogoSrc] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLogoSrc("");
    void orgSettingsApi.getCompanyLogo(orgId)
      .then(async (blob) => {
        if (!blob || blob.size === 0) {
          if (!cancelled) setLogoSrc("");
          return;
        }
        const preview = await blobToDataUrl(blob);
        if (!cancelled) setLogoSrc(preview);
      })
      .catch(() => {
        if (!cancelled) setLogoSrc("");
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (!value) return error ? <p className="settings-error">{error}</p> : <p>Loading company info…</p>;

  return (
    <Stack gap={4}>
      {error && <p role="alert" className="settings-error">{error}</p>}
      {notice && <p className="home-subtitle">{notice}</p>}
      <Card data-tour="org-company-details">
        <Stack gap={4}>
          <h2 className="home-section-title">Company info</h2>
          <p className="home-subtitle">
            These details appear on quotes, orders, and invoices: your organisation on the left,
            the customer on the right.
          </p>
          <Input
            label="Company name"
            value={value.display_name ?? ""}
            readOnly
          />
          <div className="settings-logo">
            <span className="settings-logo-label">Logo</span>
            <div className="settings-logo-frame">
              {logoSrc ? (
                <img className="settings-logo-image" src={logoSrc} alt={value.display_name || "Organisation logo"} />
              ) : (
                <p>No logo uploaded yet</p>
              )}
            </div>
            {logoError ? <p role="alert" className="settings-error">{logoError}</p> : null}
            <label className="settings-file">
              Upload logo
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                disabled={logoBusy}
                aria-label="Upload company logo"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (!file) return;
                  setLogoBusy(true);
                  setLogoError(null);
                  setError(null);
                  void orgSettingsApi.uploadCompanyLogo(orgId, file).then(async () => {
                    setNotice("Logo uploaded.");
                    reportSaved(show);
                    try {
                      setLogoSrc(await blobToDataUrl(file));
                    } catch {
                      /* Preview is best-effort; the file is already stored. */
                    }
                  }).catch((err) => {
                    const message = reportSaveFailure(show, err, "Logo could not be uploaded.");
                    setLogoError(message);
                    setError(message);
                  }).finally(() => setLogoBusy(false));
                }}
              />
            </label>
            <p className="home-subtitle">
              Recommended 400 × 120 pixels (wide rectangle, about 10:3). PNG, JPEG, WebP or GIF, up to 4 MB.
              This logo is used on quotes, orders, and invoices.
            </p>
            {logoSrc ? (
              <Button
                type="button"
                variant="secondary"
                disabled={logoBusy}
                onClick={() => {
                  setLogoBusy(true);
                  setLogoError(null);
                  setError(null);
                  void orgSettingsApi.deleteCompanyLogo(orgId).then(() => {
                    setLogoSrc("");
                    setNotice("Logo removed.");
                    reportSaved(show);
                  }).catch((err) => {
                    const message = reportSaveFailure(show, err, "Logo could not be removed.");
                    setLogoError(message);
                    setError(message);
                  }).finally(() => setLogoBusy(false));
                }}
              >
                {logoBusy ? "Working…" : "Remove logo"}
              </Button>
            ) : null}
          </div>
          <form
            onSubmit={guardSubmit((event) => {
              event.preventDefault();
              setSaving(true);
              setError(null);
              setNotice(null);
              return orgSettingsApi.saveAppSettings(orgId, settingsBody(value)).then((row) => {
                setValue(row);
                setNotice("Company details saved.");
                reportSaved(show);
                notifyLocaleChanged();
              }).catch((err) => {
                setError(reportSaveFailure(show, err, "Company details could not be saved."));
              }).finally(() => setSaving(false));
            })}
          >
            <Stack gap={4}>
              <Input
                label="Address line 1"
                value={value.company_address_line1 ?? ""}
                onChange={(event) => setValue({ ...value, company_address_line1: event.target.value })}
              />
              <Input
                label="Address line 2"
                value={value.company_address_line2 ?? ""}
                onChange={(event) => setValue({ ...value, company_address_line2: event.target.value })}
              />
              <Input
                label="Address line 3"
                value={value.company_address_line3 ?? ""}
                onChange={(event) => setValue({ ...value, company_address_line3: event.target.value })}
              />
              <Input
                label="Address line 4"
                value={value.company_address_line4 ?? ""}
                onChange={(event) => setValue({ ...value, company_address_line4: event.target.value })}
              />
              <Input
                label="Tax number"
                value={value.tax_number ?? ""}
                onChange={(event) => setValue({ ...value, tax_number: event.target.value })}
              />
              <div data-tour="org-company-website">
                <Input
                  label="Company website"
                  tooltip="Your own website, e.g. https://yourcompany.com. LeadGen needs it: AI rewrite reads it when Include my website is ticked, and your own business is never listed as a lead."
                  placeholder="https://yourcompany.com"
                  value={value.website ?? ""}
                  onChange={(event) => setValue({ ...value, website: event.target.value })}
                />
              </div>
              <Button type="submit" data-tour="org-save-company" disabled={saving}>{saving ? "Saving…" : "Save company details"}</Button>
            </Stack>
          </form>
        </Stack>
      </Card>
      <ActivityTypesSettings
        orgId={orgId}
        settings={value}
        settingsBody={settingsBody(value)}
        saving={saving}
        onSaved={setValue}
        onError={setError}
        onNotice={setNotice}
        onSaving={setSaving}
      />
    </Stack>
  );
}

const AI_SECTIONS = [
  { key: "general", label: "General" },
  { key: "leadgen", label: "Lead gen" },
  { key: "loxley", label: "Loxley" },
  { key: "mailbox", label: "Mailbox" },
];

function AiSettingsSections({ orgId }: { orgId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAccess();
  // The shared mailbox is Enterprise only, so its AI section follows the plan.
  const sections = AI_SECTIONS.filter((item) => item.key !== "mailbox" || can("crm.mailbox"));
  const requested = searchParams.get("section") || "general";
  const section = sections.some((item) => item.key === requested) ? requested : "general";
  return (
    <Stack gap={4}>
      <Tabs
        items={sections}
        activeKey={section}
        onChange={(next) => {
          const params = new URLSearchParams(searchParams);
          params.set("tab", "ai");
          if (next === "general") params.delete("section");
          else params.set("section", next);
          setSearchParams(params, { replace: true });
        }}
      />
      {section === "general" && <AiConnectionPanel orgId={orgId} />}
      {section === "leadgen" && <LeadGenLimitsCard orgId={orgId} />}
      {section === "loxley" && (
        <Stack gap={4}>
          <SettingsLoxleyPanel orgId={orgId} />
          <LoxleyGuardrailsPanel orgId={orgId} />
        </Stack>
      )}
      {section === "mailbox" && <MailboxAiSettings orgId={orgId} />}
    </Stack>
  );
}

function AppSettingsPanel({ orgId }: { orgId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { can } = useAccess();
  return (
    <Stack gap={4}>
      {error && <p role="alert" className="settings-error">{error}</p>}
      {notice && <p className="home-subtitle">{notice}</p>}
      <AppTogglesPanel orgId={orgId} setNotice={setNotice} setError={setError} />
      {can("crm.mailbox") && <MailboxSettingsCard orgId={orgId} />}
    </Stack>
  );
}

function LeadGenLimitsCard({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [row, setRow] = useState<LeadGenSearchLimits | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    leadGenApi.searchLimits(orgId).then((next) => {
      if (cancelled) return;
      setUnavailable(!next.enabled);
      setRow(next);
      setDraft(Object.fromEntries(next.limits.map((limit) => [limit.key, String(limit.value)])));
    }).catch(() => {
      if (!cancelled) setUnavailable(true);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (unavailable || (row && !row.enabled)) {
    return (
      <Card data-testid="leadgen-unavailable">
        <Stack gap={3}>
          <h2 className="home-section-title">Lead generator</h2>
          <p className="home-subtitle">
            Lead generator is off for this organization. Saleslox turns it on. Search limits appear here once it is on.
          </p>
        </Stack>
      </Card>
    );
  }
  if (!row?.enabled) return null;
  const limits = row.limits;

  function apply(next: LeadGenSearchLimits, message: string) {
    setRow(next);
    setDraft(Object.fromEntries(next.limits.map((limit) => [limit.key, String(limit.value)])));
    setNotice(message);
  }

  return (
    <Card data-testid="leadgen-limits">
      <form
        onSubmit={guardSubmit((event) => {
          event.preventDefault();
          const body: Record<string, number> = {};
          for (const limit of limits) {
            const parsed = Number(draft[limit.key]);
            if (!Number.isInteger(parsed) || parsed < limit.min || parsed > limit.max) {
              setError(reportSaveBlocked(show, `${limit.label} must be a whole number from ${limit.min} to ${limit.max}.`));
              setNotice(null);
              return undefined;
            }
            body[limit.key] = parsed;
          }
          setSaving(true);
          setError(null);
          return leadGenApi.saveSearchLimits(orgId, body).then((next) => {
            apply(next, "Lead generator settings saved. They replace the platform defaults for your organization.");
            reportSaved(show);
          }).catch((err) => {
            setError(reportSaveFailure(show, err, "Lead generator settings could not be saved."));
          }).finally(() => setSaving(false));
        })}
      >
        <Stack gap={4}>
          <h2 className="home-section-title">Lead generator limits</h2>
          <p className="home-subtitle">
            Lead generator is enabled for your organization. Saving here replaces the platform defaults for
            your searches. Businesses whose address uses the postcode you enter are always included, even when they
            are outside the search radius — a remote postcode, or one that crosses a state line, is not cut
            down to that circle. The radius only adds neighbouring postcodes. The monthly lead allowance is
            set by Saleslox and is not changed here.
          </p>
          {limits.map((limit) => (
            <LeadGenLimitField
              key={limit.key}
              limit={limit}
              value={draft[limit.key] ?? ""}
              onChange={(next) => setDraft({ ...draft, [limit.key]: next })}
            />
          ))}
          {error ? <p role="alert" className="settings-error">{error}</p> : null}
          {notice ? <p className="home-subtitle">{notice}</p> : null}
          <Cluster gap={3}>
            <Button type="submit" data-testid="leadgen-limits-save" disabled={saving}>
              {saving ? "Saving…" : "Save lead generator settings"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saving}
              onClick={() => {
                setSaving(true);
                setError(null);
                void leadGenApi.saveSearchLimits(orgId, { reset: true }).then((next) => {
                  apply(next, "Using the platform defaults again.");
                }).catch((err) => {
                  setError(err instanceof Error ? err.message : "Platform defaults could not be restored.");
                }).finally(() => setSaving(false));
              }}
            >
              Use platform defaults
            </Button>
          </Cluster>
        </Stack>
      </form>
    </Card>
  );
}

function LeadGenLimitField({
  limit,
  value,
  onChange,
}: {
  limit: LeadGenSearchLimit;
  value: string;
  onChange: (value: string) => void;
}) {
  const platformNote = limit.overridden
    ? `Your organization uses ${limit.value}, which replaces the platform setting of ${limit.platform}.`
    : `Platform setting: ${limit.platform}. Saving a different number replaces it for your organization.`;
  return (
    <div>
      <Input
        label={`${limit.label} (${limit.min}-${limit.max})`}
        tooltip={limit.help}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/[^\d]/g, ""))}
      />
      <p className="home-subtitle">
        {platformNote}
        {limit.key === "search_radius_km"
          ? " Same-postcode businesses stay in the results no matter what you set here."
          : ""}
      </p>
    </div>
  );
}

const TOGGLEABLE_APPS: Array<{ code: "timesheet" | "expense"; capability: string; label: string; description: string }> = [
  {
    code: "timesheet",
    capability: "timesheet.entries",
    label: "Timesheet",
    description: "Lets people log and submit hours, with a reports-to approval chain.",
  },
  {
    code: "expense",
    capability: "expense.reports",
    label: "Expense Manager",
    description: "Lets people submit and get expense claims approved.",
  },
];

function AppTogglesPanel({
  orgId,
  setNotice,
  setError,
}: {
  orgId: string;
  setNotice: (value: string | null) => void;
  setError: (value: string | null) => void;
}) {
  const { show } = useToast();
  const { tier, can, refresh } = useAccess();
  const [busy, setBusy] = useState<string | null>(null);

  if (tier !== "enterprise") return null;

  return (
    <Card data-tour="org-app-toggles">
      <Stack gap={4}>
        <h2 className="home-section-title">Enterprise apps</h2>
        <p className="home-subtitle">
          Timesheet and Expense are included on Enterprise and show in the top navigation while they are on. Turn one off to hide it.
        </p>
        {TOGGLEABLE_APPS.map((app) => {
          const enabled = can(app.capability);
          return (
            <div key={app.code} className="settings-long-check">
              <Checkbox
                label={`${app.label} — ${app.description}`}
                checked={enabled}
                disabled={busy === app.code}
                onChange={(event) => {
                  const nextEnabled = event.target.checked;
                  setBusy(app.code);
                  setError(null);
                  void orgSettingsApi.setAppStatus(orgId, app.code, nextEnabled).then(() => {
                    setNotice(`${app.label} ${nextEnabled ? "enabled" : "disabled"}.`);
                    reportSaved(show);
                    refresh();
                  }).catch((err) => {
                    setError(reportSaveFailure(show, err, `${app.label} could not be updated.`));
                  }).finally(() => setBusy(null));
                }}
              />
            </div>
          );
        })}
      </Stack>
    </Card>
  );
}

function SsoAiPanel({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [sso, setSso] = useState<SsoSettings | null>(null);
  const [secret, setSecret] = useState("");
  const [certificate, setCertificate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    try {
      const ssoRow = await billingApi.getSso(orgId);
      setSso(ssoRow);
    } catch (err) {
      setError(err instanceof Error ? err.message : "SSO settings could not be loaded.");
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  if (!sso) return error ? <p className="settings-error">{error}</p> : <p>Loading SSO…</p>;

  const protocol = sso.protocol === "saml" ? "saml" : "oidc";

  return (
    <Stack gap={4}>
      {error && <p role="alert" className="settings-error">{error}</p>}
      {notice && <p className="home-subtitle">{notice}</p>}
      <Card>
        <Stack gap={4}>
          <h2 className="home-section-title">Single sign-on</h2>
          <p className="home-subtitle">
            People continue with SSO from the login screen using their email. OIDC uses Authorization Code with PKCE.
            SAML uses a signed assertion. Existing members are signed in; the IdP email must match the account that
            started SSO.
          </p>
          <Checkbox
            label="Enable SSO"
            checked={sso.enabled}
            tooltip={FIELD_HINTS.sso_enabled}
            onChange={(event) => setSso({ ...sso, enabled: event.target.checked })}
          />
          <Select
            label="Protocol"
            tooltip={FIELD_HINTS.sso_protocol}
            value={protocol}
            onChange={(event) => setSso({ ...sso, protocol: event.target.value === "saml" ? "saml" : "oidc" })}
          >
            <option value="oidc">OpenID Connect (OIDC)</option>
            <option value="saml">SAML 2.0</option>
          </Select>
          <Input
            label="Allowed email domains"
            tooltip={FIELD_HINTS.sso_domains}
            value={(sso.domains ?? []).join(", ")}
            onChange={(event) =>
              setSso({
                ...sso,
                domains: event.target.value.split(/[,\s]+/).map((item) => item.trim()).filter(Boolean),
              })
            }
            placeholder="acme.com, acme.co.uk"
          />
          <Input
            label="Metadata URL"
            tooltip={FIELD_HINTS.sso_metadata_url}
            value={sso.metadata_url ?? ""}
            onChange={(event) => setSso({ ...sso, metadata_url: event.target.value })}
            placeholder={protocol === "oidc" ? "https://idp.example/.well-known/openid-configuration" : "https://idp.example/metadata.xml"}
          />
          {protocol === "oidc" ? (
            <>
              <Input
                label="Issuer"
                tooltip={FIELD_HINTS.sso_issuer}
                value={sso.issuer}
                onChange={(event) => setSso({ ...sso, issuer: event.target.value })}
              />
              <Input
                label="Client ID"
                tooltip={FIELD_HINTS.sso_client_id}
                value={sso.client_id ?? ""}
                onChange={(event) => setSso({ ...sso, client_id: event.target.value || null })}
              />
              <Input
                label="Client secret"
                tooltip={FIELD_HINTS.sso_client_secret}
                type="password"
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                placeholder={sso.configured ? "Leave blank to keep the stored secret" : undefined}
              />
              <Input
                label="Scopes"
                tooltip={FIELD_HINTS.sso_scopes}
                value={sso.scopes ?? "openid email profile"}
                onChange={(event) => setSso({ ...sso, scopes: event.target.value })}
              />
              <Input label="Redirect URI (paste into the IdP)" tooltip={FIELD_HINTS.sso_redirect_uri} value={sso.oidc_redirect_uri ?? ""} readOnly />
            </>
          ) : (
            <>
              <Input
                label="IdP SSO URL"
                tooltip={FIELD_HINTS.sso_issuer}
                value={sso.issuer}
                onChange={(event) => setSso({ ...sso, issuer: event.target.value })}
              />
              <Input
                label="SP entity ID"
                tooltip={FIELD_HINTS.sso_entity_id}
                value={sso.entity_id ?? sso.saml_entity_id ?? ""}
                onChange={(event) => setSso({ ...sso, entity_id: event.target.value })}
              />
              <Input label="Assertion Consumer Service URL" tooltip={FIELD_HINTS.sso_acs_url} value={sso.saml_acs_url ?? ""} readOnly />
              <div style={{ display: "grid", gap: "var(--space-1)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
                  <label htmlFor="sso-saml-certificate">IdP signing certificate</label>
                  <HintMark label="About IdP signing certificate" text={FIELD_HINTS.sso_certificate} />
                </span>
                <textarea
                  id="sso-saml-certificate"
                  value={certificate}
                  onChange={(event) => setCertificate(event.target.value)}
                  placeholder={sso.certificate_configured ? "Leave blank to keep the stored certificate" : "-----BEGIN CERTIFICATE-----"}
                  rows={6}
                  style={{
                    width: "100%",
                    minHeight: "var(--touch-target)",
                    padding: "var(--space-3)",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--color-border)",
                    background: "var(--color-input)",
                    color: "var(--color-text)",
                    fontSize: "var(--font-size-sm)",
                    fontFamily: "var(--font-sans)",
                  }}
                />
              </div>
            </>
          )}
          <Button
            type="button"
            onClick={() => {
              setError(null);
              void billingApi.saveSso(orgId, {
                issuer: sso.issuer,
                client_id: sso.client_id,
                enabled: sso.enabled,
                mock_mode: false,
                client_secret: secret || undefined,
                protocol,
                domains: sso.domains ?? [],
                metadata_url: sso.metadata_url ?? "",
                scopes: sso.scopes ?? "openid email profile",
                entity_id: sso.entity_id ?? "",
                certificate: certificate || undefined,
              }).then((row) => {
                setSso(row);
                setSecret("");
                setCertificate("");
                setNotice("SSO settings saved.");
                reportSaved(show);
              }).catch((err) => setError(reportSaveFailure(show, err, "SSO could not be saved.")));
            }}
          >
            Save SSO
          </Button>
        </Stack>
      </Card>
    </Stack>
  );
}

function ImportPanel({
  orgId,
  canPeople,
  canCrmImport,
}: {
  orgId: string;
  canPeople: boolean;
  canCrmImport: boolean;
}) {
  const [kind, setKind] = useState<"users" | "crm" | "roles">(canCrmImport ? "crm" : "users");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mappingDraft, setMappingDraft] = useState<Record<string, Record<string, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const description = useMemo(() => {
    if (kind === "users") {
      return "Download the users template. Email is required, and crm_role is app_user or app_admin. You can also set department, org admin, a CRM seat, reports-to email, a territory code, and a named role that already exists here. Passwords are never imported.";
    }
    if (kind === "roles") {
      return "Download the roles template. Name each role, set record_scope to own, territory, or all, and list its tabs separated by semicolons. Custom roles require the Professional plan.";
    }
    return "Download the companies, contacts, leads, opportunities, products, agreements, and agreement lines zip. Keep the same external_key on a company and every contact, lead, or deal that belongs to it. Every product needs its own external_key and a SKU. Dates are YYYY-MM-DD. Link products with product_external_keys (use key:quantity on a deal). After upload, check the mapping, fix any issues, then commit.";
  }, [kind]);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const result = kind === "users"
        ? await orgSettingsApi.previewUsers(orgId, file)
        : kind === "roles"
          ? await orgSettingsApi.previewRoles(orgId, file)
          : await orgSettingsApi.previewCrm(orgId, file);
      setPreview(result);
      setMappingDraft(result.mapping ?? {});
    } catch (err) {
      setError(err instanceof Error ? err.message : "The file could not be read.");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function remap() {
    if (!preview || kind !== "crm") return;
    setBusy(true);
    setError(null);
    try {
      const result = await orgSettingsApi.mapCrm(orgId, preview.import_id, mappingDraft);
      setPreview(result);
      setMappingDraft(result.mapping ?? mappingDraft);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The mapping could not be applied.");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const result = kind === "users"
        ? await orgSettingsApi.commitUsers(orgId, preview.import_id)
        : kind === "roles"
          ? await orgSettingsApi.commitRoles(orgId, preview.import_id)
          : await orgSettingsApi.commitCrm(orgId, preview.import_id);
      setPreview(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The import could not be committed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={5}>
      <Card>
        <Stack gap={4}>
          <h2 className="home-section-title">Load historic data</h2>
          <p className="home-subtitle">{description}</p>
          <Cluster gap={3} align="center">
            {canPeople && canCrmImport && (
              <Select
                tooltip={FIELD_HINTS.import_kind}
                value={kind}
                onChange={(event) => { setKind(event.target.value as "users" | "crm" | "roles"); setPreview(null); }}
              >
                <option value="crm">Companies, contacts, leads, deals, products, and agreements</option>
                <option value="users">Users</option>
                <option value="roles">Roles</option>
              </Select>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={() => (
                kind === "users"
                  ? orgSettingsApi.downloadUserTemplate(orgId)
                  : kind === "roles"
                    ? orgSettingsApi.downloadRolesTemplate(orgId)
                    : orgSettingsApi.downloadCrmTemplate(orgId)
              )}
            >
              Download template
            </Button>
            <label className="settings-file">
              Upload filled template
              <input
                type="file"
                accept={kind === "crm" ? ".zip,.csv,application/zip" : ".csv,.zip,text/csv"}
                onChange={(event) => void onFile(event.target.files?.[0])}
              />
            </label>
          </Cluster>
          {!canPeople && kind === "users" && (
            <p className="home-subtitle">Only organization admins can import users.</p>
          )}
          {!canPeople && kind === "roles" && (
            <p className="home-subtitle">Only organization admins can import roles.</p>
          )}
        </Stack>
      </Card>
      {error && <p role="alert" className="settings-error">{error}</p>}
      {busy && <p>Working…</p>}
      {preview && (
        <Card>
          <Stack gap={3}>
            <Cluster gap={4} align="center">
              <strong>{preview.valid_count} valid</strong>
              <span>{preview.invalid_count} need fixes</span>
              <Button type="button" disabled={!preview.can_commit || preview.status === "committed" || busy} onClick={() => commit()}>
                {preview.status === "committed" ? "Loaded" : "Commit import"}
              </Button>
            </Cluster>
            {kind === "crm" && preview.detected_headers && Object.keys(preview.detected_headers).length > 0 && preview.status !== "committed" && (
              <Stack gap={3}>
                <p className="home-subtitle">Match each platform field to a column in your file, then re-check. Unmapped or invalid rows are listed as issues.</p>
                {Object.entries(preview.detected_headers).map(([recordType, headers]) => (
                  <div key={recordType}>
                    <strong>{recordType}</strong>
                    <table className="crm-table">
                      <thead>
                        <tr>
                          <th>Platform field</th>
                          <th>Your column</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.canonical_fields?.[recordType] ?? []).map((field) => (
                          <tr key={field}>
                            <td>{field}</td>
                            <td>
                              <Select
                                aria-label={`${recordType} ${field} column`}
                                value={mappingDraft[recordType]?.[field] ?? ""}
                                onChange={(event) => {
                                  const next = { ...mappingDraft };
                                  next[recordType] = { ...(next[recordType] ?? {}), [field]: event.target.value };
                                  setMappingDraft(next);
                                }}
                              >
                                <option value="">Not mapped</option>
                                {headers.map((header) => (
                                  <option key={header} value={header}>{header}</option>
                                ))}
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                <Button type="button" variant="secondary" disabled={busy} onClick={() => remap()}>
                  Re-check mapping
                </Button>
              </Stack>
            )}
            {(preview.issues ?? []).length > 0 && (
              <Stack gap={2}>
                <strong>Issues</strong>
                <ul>
                  {(preview.issues ?? []).slice(0, 50).map((issue, index) => (
                    <li key={`${issue.record_type}-${issue.row_number ?? index}-${issue.message}`}>
                      {issue.record_type}
                      {issue.row_number ? ` row ${issue.row_number}` : ""}: {issue.message}
                    </li>
                  ))}
                </ul>
              </Stack>
            )}
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Type</th>
                  <th>Key</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={`${row.record_type}-${row.row_number}`}>
                    <td>{row.row_number}</td>
                    <td>{row.record_type}</td>
                    <td>{row.external_key}</td>
                    <td>{row.status}</td>
                    <td>{row.errors.join(" ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function RecyclePanel({ orgId }: { orgId: string }) {
  const [items, setItems] = useState<RecycleItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { formatDateTime } = useLocale();

  async function load() {
    try {
      setItems((await opsApi.listRecycle(orgId)).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Recycle bin could not be loaded.");
    }
  }

  async function restore(item: RecycleItem) {
    setError(null);
    setNotice(null);
    try {
      const result = await opsApi.restoreRecycle(orgId, item.source, item.id);
      setNotice(result.message || `Restored ${item.title ?? item.label}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Record could not be restored.");
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  return (
    <Stack gap={4}>
      <p className="home-subtitle">
        Deleted records stay here for 30 days and are then removed automatically. Restore a record to put it back.
        The bin cannot be emptied by hand.
      </p>
      {error && <p className="crm-error">{error}</p>}
      {notice && <p className="crm-muted" role="status">{notice}</p>}
      {items.length === 0 ? (
        <EmptyState title="Recycle bin is empty" description="Deleted companies, contacts, leads, sales cycles, and related records appear here." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr><th>Type</th><th>Record</th><th>Deleted</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={`${item.source}-${item.id}`}>
                <td>{item.label}</td>
                <td>{item.title ?? item.detail ?? item.id}</td>
                <td>{item.soft_deleted_at ? formatDateTime(item.soft_deleted_at) : "—"}</td>
                <td>
                  <Button variant="secondary" size="sm" onClick={() => restore(item)}>
                    Restore
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Stack>
  );
}

function formatAuditValue(value: unknown): string {
  if (value == null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function AuditPanel({ orgId }: { orgId: string }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [query, setQuery] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const { formatDateTime } = useLocale();

  async function load(nextOffset = 0) {
    try {
      const result = await orgSettingsApi.listAuditLog(orgId, { q: query, offset: nextOffset });
      setEvents((current) => (nextOffset === 0 ? result.events : [...current, ...result.events]));
      setHasMore(result.has_more);
      setError(null);
      if (nextOffset === 0) setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Audit log could not be loaded.");
    }
  }

  useEffect(() => {
    void load(0);
  }, [orgId]);

  return (
    <Stack gap={4}>
      <p className="home-subtitle">
        Every signed-in change in this organization, including logins, people, billing, and app records.
        Only organization admins can open this list.
      </p>
      {error && <p className="crm-error">{error}</p>}
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void load(0);
        }}
      >
        <Cluster gap={3}>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search action, record, or person"
          />
          <Button type="submit">Search</Button>
        </Cluster>
      </form>
      {events.length === 0 ? (
        <EmptyState title="No audit events yet" description="Create, update, or sign-in actions will appear here." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Record</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={`${event.source}-${event.id}`}>
                <td>{event.created_at ? formatDateTime(event.created_at) : "—"}</td>
                <td>{event.actor_name || event.actor_user_id || "System"}</td>
                <td>{event.action}</td>
                <td>
                  <Cluster gap={2}>
                    <span>
                      {event.resource_label || event.resource_type}
                      {(event.resource_code || event.resource_id) ? ` ${event.resource_code || event.resource_id}` : ""}
                    </span>
                    <Button variant="secondary" size="sm" type="button" onClick={() => setSelected(event)}>
                      Details
                    </Button>
                  </Cluster>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {hasMore && (
        <Button variant="secondary" type="button" onClick={() => load(events.length)}>
          Load more
        </Button>
      )}
      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.action : "Audit event"}
        footer={
          <Button type="button" variant="secondary" onClick={() => setSelected(null)}>
            Close
          </Button>
        }
      >
        {selected && (
          <Stack gap={3}>
            <p className="home-subtitle">
              {selected.actor_name || selected.actor_user_id || "System"}
              {selected.created_at ? ` ${formatDateTime(selected.created_at)}` : ""}
              {selected.ip ? ` ${selected.ip}` : ""}
            </p>
            <p className="home-subtitle">
              {selected.resource_label || selected.resource_type}
              {(selected.resource_code || selected.resource_id) ? ` ${selected.resource_code || selected.resource_id}` : ""}
            </p>
            {selected.record_guid && (
              <p className="home-subtitle">Record GUID: {selected.record_guid}</p>
            )}
            {selected.old_value != null && (
              <label>
                Before
                <textarea className="crm-textarea" readOnly rows={6} value={formatAuditValue(selected.old_value)} />
              </label>
            )}
            {selected.new_value != null && (
              <label>
                After
                <textarea className="crm-textarea" readOnly rows={6} value={formatAuditValue(selected.new_value)} />
              </label>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
